import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, Save, X, Package, User, Eye, EyeOff, Images, Camera } from "lucide-react";
import PhotoPicker from '../components/PhotoPicker';
import SitePhotosTab from '../components/SitePhotosTab';
import PillSelect from '../components/PillSelect';
import '../admin.css';
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { Logo } from "../components/Logo";
import { CATEGORIES } from "../data/categories";
import { resolveBusinessPhone } from '../lib/contactDetails';

const emptyItem = { name: "", category: "electriques", description: "", day: "", week: "", active: true, image_url: "" };

const TABS = [
  { id: "inventory", label: "Inventaire", icon: Package },
  { id: "photos", label: "Photos du site", icon: Images },
  { id: "profile", label: "Profil entreprise", icon: User },
];

const ItemForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial || emptyItem);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try { await onSave({
      ...form,
      day: form.day === "" || form.day == null ? null : Number(form.day),
      week: form.week === "" || form.week == null ? null : Number(form.week),
    }); } catch { setError('Enregistrement impossible. Vos modifications sont conservées; réessayez.'); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="bg-[#F8FAFC] border border-[#E87B3A] rounded-sm p-6 space-y-4" data-testid="item-form" aria-busy={saving}>
      <h3 className="admin-form-title">{initial ? 'Modifier cet article' : 'Nouvel article'}</h3>
      <fieldset disabled={saving} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]" htmlFor="item-form-name">Nom *</label>
          <input required value={form.name} onChange={update("name")} id="item-form-name" data-testid="item-form-name"
            className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A]" />
        </div>
        <div>
          <label className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]" htmlFor="item-form-category">Catégorie *</label>
          <PillSelect value={form.category} onChange={category => setForm(prev => ({ ...prev, category }))} options={CATEGORIES.filter(c => c.id !== 'all')} testId="item-form-category" label="Catégorie de l’article" />
        </div>
      </div>
      <div>
        <label className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]" htmlFor="item-form-description">Description</label>
        <input value={form.description || ""} onChange={update("description")} id="item-form-description" data-testid="item-form-description"
          className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A]" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]" htmlFor="item-form-day">Prix Jour ($)</label>
          <input type="number" min="0" step="0.01" value={form.day ?? ""} onChange={update("day")} id="item-form-day" data-testid="item-form-day"
            placeholder="Vide = Sur demande"
            className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A]" />
        </div>
        <div>
          <label className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]" htmlFor="item-form-week">Prix Semaine ($)</label>
          <input type="number" min="0" step="0.01" value={form.week ?? ""} onChange={update("week")} id="item-form-week" data-testid="item-form-week"
            placeholder="Vide = Sur demande"
            className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A]" />
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.active} onChange={update("active")} data-testid="item-form-active" className="w-4 h-4 accent-[#E87B3A]" />
        <span className="font-body text-sm text-[#0F172A]">Article actif (visible sur le site public)</span>
      </label>

      <PhotoPicker label="Photo de l’article" value={form.image_url || ''} onChange={image_url => setForm(prev => ({ ...prev, image_url }))} onBusyChange={setProcessing} disabled={saving} testId="item-form-image" />
      </fieldset>
      {error && <p role="alert" className="admin-error">{error}</p>}
      <div className="flex flex-wrap gap-2 pt-2">
        <button type="submit" data-testid="item-form-save"
          disabled={saving || processing}
          className="inline-flex items-center gap-2 bg-[#E87B3A] hover:bg-[#D96C2A] text-white px-5 py-2.5 rounded-sm font-body font-bold uppercase tracking-wider text-sm">
          <Save size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button type="button" onClick={onCancel} data-testid="item-form-cancel"
          disabled={saving || processing}
          className="inline-flex items-center gap-2 bg-white border border-[#E2E8F0] text-[#0F172A] px-5 py-2.5 rounded-sm font-body font-bold uppercase tracking-wider text-sm hover:border-[#0F172A]">
          <X size={14} /> Annuler
        </button>
      </div>
    </form>
  );
};

const InventoryTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get("/admin/items");
      setItems(data);
    } catch { setError('Impossible de charger les articles. Réessayez.'); } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (payload) => {
    await api.post("/admin/items", payload);
    setCreating(false);
    await load();
  };
  const update = async (id, payload) => {
    await api.put(`/admin/items/${id}`, payload);
    setEditing(null);
    await load();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    setBusy(true); setError('');
    try { await api.delete(`/admin/items/${id}`); await load(); }
    catch { setError('Suppression impossible. L’article est conservé.'); }
    finally { setBusy(false); }
  };
  const toggleActive = async (it) => {
    setBusy(true); setError('');
    try { await api.put(`/admin/items/${it.id}`, { active: !it.active }); await load(); }
    catch { setError('Impossible de modifier la visibilité. Réessayez.'); }
    finally { setBusy(false); }
  };

  const filtered = items.filter((it) => {
    const catOk = filter === "all" || it.category === filter;
    const q = query.trim().toLowerCase();
    const qOk = !q || it.name.toLowerCase().includes(q);
    return catOk && qOk;
  });

  return (
    <div data-testid="inventory-tab">
      {error && <p role="alert" className="admin-error">{error}<button className="admin-action" onClick={load}>Réessayer</button></p>}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl text-[#1A4E72] uppercase">Inventaire</h2>
          <p className="font-body text-sm text-[#475569]">{items.length} articles ({items.filter(i => i.active).length} actifs)</p>
        </div>
        <button onClick={() => { setCreating(true); setEditing(null); }} data-testid="add-item-button"
          className="inline-flex items-center gap-2 bg-[#E87B3A] hover:bg-[#D96C2A] text-white px-5 py-3 rounded-sm font-body font-bold uppercase tracking-wider text-sm">
          <Plus size={16} /> Ajouter un article
        </button>
      </div>

      {creating && (
        <div className="mb-6">
          <ItemForm onSave={create} onCancel={() => setCreating(false)} />
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Rechercher…" aria-label="Rechercher un article" data-testid="admin-search"
          className="px-3 py-2 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A] md:max-w-xs flex-1" />
        <PillSelect value={filter} onChange={setFilter} options={CATEGORIES} testId="admin-filter" label="Filtrer les articles par catégorie" />
      </div>

      {loading ? (
        <p className="font-body text-[#475569]">Chargement…</p>
      ) : (
        <div className="border border-[#E2E8F0] rounded-sm overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F1F5F9] border-b border-[#E2E8F0]">
              <tr className="text-left">
                <th className="px-4 py-3 font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]">Nom</th>
                <th className="px-4 py-3 font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]">Catégorie</th>
                <th className="px-4 py-3 font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72] text-right">Jour</th>
                <th className="px-4 py-3 font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72] text-right">Semaine</th>
                <th className="px-4 py-3 font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72] text-center">État</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <React.Fragment key={it.id}>
                  <tr className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`row-${it.id}`}>
                    <td className="px-4 py-3 font-body text-sm font-bold text-[#0F172A]">
                      <div className="flex items-center gap-3">
                        <button type="button" className="admin-photo-shortcut" onClick={() => { setEditing(it.id); setCreating(false); }} aria-label={`Changer la photo de ${it.name}`} data-testid={`photo-${it.id}`}>
                        {it.image_url ? (
                          <img src={it.image_url} alt="" className="w-12 h-12 object-cover rounded-sm border border-[#E2E8F0] shrink-0" />
                        ) : (
                          <span className="admin-photo-empty"><Camera size={19} /></span>
                        )}
                        </button>
                        <div>
                          {it.name}
                          <div className="font-body text-xs text-[#475569] mt-0.5 font-normal">{it.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-body text-xs uppercase tracking-wider text-[#3A6D3B]">{CATEGORIES.find(c => c.id === it.category)?.label || it.category}</td>
                    <td data-label="Jour" className="px-4 py-3 font-body text-sm text-right">{it.day != null ? `${it.day} $` : "—"}</td>
                    <td data-label="Semaine" className="px-4 py-3 font-body text-sm text-right">{it.week != null ? `${it.week} $` : "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <button onClick={() => toggleActive(it)} data-testid={`toggle-${it.id}`}
                        disabled={busy} aria-label={`${it.active ? 'Masquer' : 'Afficher'} ${it.name}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider ${it.active ? "bg-[#3A6D3B]/10 text-[#3A6D3B]" : "bg-[#94A3B8]/20 text-[#475569]"}`}>
                        {it.active ? <><Eye size={11} /> Actif</> : <><EyeOff size={11} /> Caché</>}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => { setEditing(it.id); setCreating(false); }} data-testid={`edit-${it.id}`}
                        aria-label={`Modifier ${it.name}`}
                        className="p-2 text-[#1A4E72] hover:bg-[#E2E8F0] rounded-sm"><Pencil size={14} /></button>
                      <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`}
                        disabled={busy} aria-label={`Supprimer ${it.name}`}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                  {editing === it.id && (
                    <tr className="bg-[#F8FAFC] admin-editor-row">
                      <td colSpan={6} className="p-4">
                        <ItemForm initial={it} onSave={(p) => update(it.id, p)} onCancel={() => setEditing(null)} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-12 text-center font-body text-sm text-[#475569]">Aucun article.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ProfileTab = () => {
  const [profile, setProfile] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/profile").then(({ data }) => setProfile({ ...data, phone: resolveBusinessPhone(data.phone) })).catch(() => setError('Impossible de charger le profil. Rechargez la page pour réessayer.'));
  }, []);

  const update = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSaved(false);
    try { await api.put("/admin/profile", profile); setSaved(true); }
    catch { setError('Enregistrement impossible. Vos modifications sont conservées; réessayez.'); }
    finally { setSaving(false); }
  };

  if (!profile) return <p className={error ? 'admin-error' : 'font-body text-[#475569]'} role={error ? 'alert' : 'status'}>{error || 'Chargement…'}</p>;

  const fields = [
    ["business_name", "Nom de l’entreprise"],
    ["tagline", "Slogan"],
    ["contact_name", "Personne contact"],
    ["phone", "Téléphone"],
    ["email", "Courriel"],
    ["address", "Adresse"],
    ["service_area", "Zone de service"],
  ];

  return (
    <div data-testid="profile-tab">
      <h2 className="font-display text-3xl text-[#1A4E72] uppercase">Profil entreprise</h2>
      <p className="font-body text-sm text-[#475569] mb-6">Ces informations sont affichées partout sur le site public.</p>

      <form onSubmit={save} className="bg-white border border-[#E2E8F0] rounded-sm p-6 space-y-4 max-w-2xl" data-testid="profile-form">
        {error && <p className="admin-error" role="alert">{error}</p>}
        <fieldset disabled={saving} className="space-y-4">
        {fields.map(([key, label]) => (
          <div key={key}>
            <label htmlFor={`profile-${key}`} className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]">{label}</label>
            <input id={`profile-${key}`} type={key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'} value={profile[key] || ""} onChange={update(key)} data-testid={`profile-${key}`}
              className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A]" />
          </div>
        ))}
        <div>
          <label htmlFor="profile-hours" className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]">Heures d’ouverture</label>
          <textarea id="profile-hours" rows={3} value={profile.hours || ""} onChange={update("hours")} data-testid="profile-hours"
            className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A] resize-none" />
        </div>
        <div>
          <label htmlFor="profile-about" className="font-body text-xs font-bold uppercase tracking-widest text-[#1A4E72]">À propos</label>
          <textarea id="profile-about" rows={4} value={profile.about || ""} onChange={update("about")} data-testid="profile-about"
            className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded-sm font-body focus:outline-none focus:border-[#E87B3A] resize-none" />
        </div>

        </fieldset>
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" data-testid="profile-save" disabled={saving}
            className="inline-flex items-center gap-2 bg-[#E87B3A] hover:bg-[#D96C2A] text-white px-6 py-3 rounded-sm font-body font-bold uppercase tracking-wider text-sm">
            <Save size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          {saved && <span className="font-body text-sm text-[#3A6D3B] font-bold" data-testid="profile-saved">✓ Profil enregistré</span>}
        </div>
      </form>
    </div>
  );
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inventory");
  const [photosDirty, setPhotosDirty] = useState(false);
  const canLeave = () => !photosDirty || window.confirm('Quitter sans enregistrer les changements de photos ?');

  const handleLogout = () => {
    if (!canLeave()) return;
    if (process.env.REACT_APP_LOCAL_PREVIEW === 'true') { navigate('/'); return; }
    logout();
    navigate("/admin/login");
  };

  return (
    <main className="min-h-screen bg-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 bg-[#0F172A] text-white border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo size={40} />
            <div>
              <div className="font-display text-2xl text-white uppercase leading-none">
                LOCATION <span className="text-[#E87B3A]">RNC</span>
              </div>
              <div className="font-body text-[10px] font-bold uppercase tracking-[0.25em] text-white/50 mt-1">
                Espace admin
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site"
              onClick={e => { if (!canLeave()) e.preventDefault(); }}
              className="inline-flex font-body text-xs font-bold tracking-wider text-white/70 hover:text-[#E87B3A]">
              Voir le site public →
            </Link>
            <span className="hidden md:inline font-body text-xs text-white/50">{user?.email}</span>
            <button onClick={handleLogout} data-testid="admin-logout"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-[#E87B3A] text-white px-4 py-2 rounded-sm font-body font-bold uppercase tracking-wider text-xs transition-colors">
              <LogOut size={14} /> {process.env.REACT_APP_LOCAL_PREVIEW === 'true' ? 'Quitter l’atelier' : 'Déconnexion'}
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 lg:px-12 py-10">
        {process.env.REACT_APP_LOCAL_PREVIEW === 'true' && <div className="admin-local-notice" role="note">Atelier local · Les changements sont enregistrés dans ce navigateur. Le site en ligne reste inchangé.</div>}
        <div className="flex gap-2 mb-8 border-b border-[#E2E8F0]" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => { if (t.id !== tab && canLeave()) setTab(t.id); }} data-testid={`tab-${t.id}`}
              aria-pressed={tab === t.id}
              className={`inline-flex items-center gap-2 px-5 py-3 font-body text-sm font-bold uppercase tracking-wider border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-[#E87B3A] text-[#E87B3A]" : "border-transparent text-[#475569] hover:text-[#1A4E72]"
              }`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "inventory" && <InventoryTab />}
        {tab === "photos" && <SitePhotosTab onDirtyChange={setPhotosDirty} />}
        {tab === "profile" && <ProfileTab />}
      </div>
    </main>
  );
}
